// frontend/smartdocs-admin/src/components/chat/ChatInput.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react'; // Added useRef and useEffect
import Button from "@shared/Button";

interface ChatInputProps {
  onSendMessage: (text: string, direction: 'ltr' | 'rtl') => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage }) => {
  const [input, setInput] = useState('');
  const [textDirection, setTextDirection] = useState<'ltr' | 'rtl'>('ltr'); // State to manage text direction
  const textareaRef = useRef<HTMLTextAreaElement>(null); // Ref for the textarea element

  // Function to detect text direction (simplified for common RTL languages)
  const detectTextDirection = useCallback((text: string): 'ltr' | 'rtl' => {
    // A simple heuristic: check for common RTL characters
    const rtlRegex = /[֐-׿؀-ۿݐ-ݿࢠ-ࣿﭐ-﷏ﷰ-﷿ﹰ-﻿]/;
    return rtlRegex.test(text) ? 'rtl' : 'ltr';
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      // Pass the detected text direction along with the message
      onSendMessage(input, textDirection);
      setInput('');
      setTextDirection('ltr'); // Reset direction after sending
    }
  };

  // Handles key down events for the textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // If Enter key is pressed
    if (e.key === 'Enter') {
      // If Shift key is NOT held, submit the message
      if (!e.shiftKey) {
        e.preventDefault(); // Prevent default new line behavior
        handleSubmit(e); // Submit the form
      }
      // If Shift key IS held, allow default new line behavior (no action needed here)
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);
    // Update text direction dynamically as user types
    setTextDirection(detectTextDirection(value));
  };

  // Effect to adjust textarea height dynamically
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'; // Reset height to recalculate
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'; // Set height to scrollHeight
    }
  }, [input]); // Re-run when input changes

  return (
    <form onSubmit={handleSubmit} className="flex items-center space-x-2">
      <textarea
        ref={textareaRef} // Attach the ref
        value={input}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown} // Add the new key down handler
        placeholder="Type your message..."
        className="flex-1 px-2 text-sm min-h-12 max-h-21 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 overflow-y-auto" // Added min-h, max-h, and overflow-y-auto, removed rows
        dir={textDirection} // Dynamically set text direction
        style={{ textAlign: textDirection === 'rtl' ? 'right' : 'left' }} // Apply text alignment
      />
      <Button
        type="submit"
        className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        variant="primary"
      >
        Send
      </Button>
    </form>
  );
};

export default ChatInput;
